import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useContent } from "@/context/ContentContext";

export default function Pricing() {
  const { content } = useContent();
  const projects = content.projects || [];
  const factors = content.pricingFactors || [];
  const site = content.site || {};
  const intro = content.pricingIntro || {};
  const r1 = useReveal();
  const r2 = useReveal();
  const r3 = useReveal();
  return (
    <section data-testid="pricing-section" className="bg-[#f9f8f6]">
      {/* Antraštė */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-[140px] md:pt-[180px] pb-12 md:pb-20">
        <div className="reveal grid grid-cols-1 md:grid-cols-12 gap-8 items-end" ref={r1}>
          <div className="md:col-span-7">
            <span className="overline">— 03 / Kainos</span>
            <h1 className="mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-[#1a1a1a]">
              Kainos.
            </h1>
          </div>
          <div className="md:col-span-5 md:text-right">
            <p className="text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light max-w-md md:ml-auto italic">
              „{site.authorQuote}“
            </p>
          </div>
        </div>
      </div>

      {/* Įžangos tekstas */}
      <div className="border-t border-[#e5e2dd]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12 reveal" ref={r2}>
          <div className="md:col-span-5">
            <h2 className="font-serif-display text-3xl md:text-4xl leading-snug text-[#1a1a1a] max-w-md">
              {intro.headline || "Kiekviena pirtis – individuali."}
            </h2>
          </div>
          <div className="md:col-span-7 text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light space-y-6 max-w-2xl">
            {(intro.body || []).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </div>

      {/* Veiksniai */}
      <div className="border-t border-[#e5e2dd] bg-[#efedea]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="overline">Kainai įtakos turi</span>
            <h2 className="mt-6 font-serif-display text-3xl md:text-4xl leading-snug text-[#1a1a1a] max-w-md">
              Šeši pagrindiniai veiksniai.
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 border-t border-[#d8d4ce]">
              {factors.map((f, i) => (
                <li
                  key={f}
                  data-testid={`pricing-factor-${i}`}
                  className="flex gap-4 py-5 px-1 border-b border-[#d8d4ce] sm:[&:nth-child(odd)]:border-r sm:[&:nth-child(odd)]:pr-6 sm:[&:nth-child(even)]:pl-6"
                >
                  <span className="font-serif-display italic text-[#a66e4b] text-lg shrink-0">
                    0{i + 1}
                  </span>
                  <span className="text-[15px] text-[#3a3a3a] font-light leading-relaxed">
                    {f}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Orientacinės kainos */}
      <div className="border-t border-[#e5e2dd]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-32 reveal" ref={r3}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
            <div className="md:col-span-7">
              <span className="overline">— Orientacinės kainos</span>
              <h2 className="mt-6 font-serif-display text-3xl md:text-5xl leading-tight tracking-tight text-[#1a1a1a]">
                Trys modeliai. <span className="italic">Aiškios pradinės kainos.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:text-right">
              <p className="text-[#3a3a3a] text-sm md:text-base font-light max-w-sm md:ml-auto">
                Po pokalbio ir poreikių aptarimo parengiamas individualus
                pasiūlymas, atitinkantis būtent jūsų projektą.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[#e5e2dd]">
            {projects.map((p, i) => (
              <PriceCard key={p.id} project={p} last={i === projects.length - 1} />
            ))}
          </div>

          <p className="mt-12 text-center text-sm text-[#5c5c5c] font-light italic max-w-2xl mx-auto">
            Galutinė kaina priklauso nuo pasirinktos komplektacijos ir
            individualių sprendimų. {site.seasonNote}
          </p>

          <div className="mt-12 flex justify-center">
            <Link to="/kontaktai" data-testid="pricing-cta" className="btn-primary">
              Gauti individualų pasiūlymą <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function PriceCard({ project, last }) {
  return (
    <div
      data-testid={`price-card-${project.id}`}
      className={`p-8 md:p-10 border-b border-[#e5e2dd] md:border-b-0 md:border-r ${
        last ? "md:border-r-0" : ""
      }`}
    >
      <span className="overline">{project.label}</span>
      <h3 className="mt-4 font-serif-display text-2xl md:text-3xl text-[#1a1a1a] leading-tight">
        {project.priceShort}
      </h3>
      <div className="mt-3 font-serif-display italic text-[#a66e4b] text-2xl md:text-3xl">
        {project.priceFrom}
      </div>
      <p className="mt-6 text-sm text-[#3a3a3a] font-light leading-relaxed min-h-[72px]">
        {project.priceNote}
      </p>
      <div className="mt-8 pt-6 border-t border-[#e5e2dd] flex items-center justify-between">
        <Link
          to={`/projektai#${project.slug}`}
          className="text-[11px] uppercase tracking-[0.22em] text-[#1a1a1a] hover:text-[#a66e4b]"
        >
          {project.name}
        </Link>
        <span className="overline">{project.area}</span>
      </div>
    </div>
  );
}
